import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  getUsersWithPinStatus,
  setUserPin,
  UserWithPinStatus,
} from "../utils/supabase";
import toast from "react-hot-toast";
import {
  KeyIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ClockIcon,
  UserIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

interface PinManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SetPinModalProps {
  user: UserWithPinStatus | null;
  isOpen: boolean;
  onClose: () => void;
  onPinSet: () => void;
}

const SetPinModal: React.FC<SetPinModalProps> = ({
  user,
  isOpen,
  onClose,
  onPinSet,
}) => {
  const { user: currentUser } = useAuth();
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPin("");
      setConfirmPin("");
      setError("");
    }
  }, [isOpen]);

  const validatePin = (pinValue: string): string | null => {
    if (pinValue.length !== 4) {
      return "PIN must be exactly 4 digits";
    }

    if (!/^\d{4}$/.test(pinValue)) {
      return "PIN must contain only digits";
    }

    // Check for all same digits
    if (/^(.)\1{3}$/.test(pinValue)) {
      return "PIN cannot be all the same digits (e.g., 1111)";
    }

    // Check for sequential patterns
    const sequential = [
      "1234",
      "2345",
      "3456",
      "4567",
      "5678",
      "6789",
      "0123",
      "4321",
      "5432",
      "6543",
      "7654",
      "8765",
      "9876",
      "3210",
    ];

    if (sequential.includes(pinValue)) {
      return "PIN cannot be sequential (e.g., 1234, 4321)";
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentUser) {
      return;
    }

    // Validate PIN
    const pinError = validatePin(pin);
    if (pinError) {
      setError(pinError);
      return;
    }

    if (pin !== confirmPin) {
      setError("PINs do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await setUserPin(user.id, pin, currentUser.id);

      if (result.success) {
        toast.success(`PIN set for ${user.full_name}`);
        onPinSet();
        onClose();
      } else {
        setError(result.error_message || "Failed to set PIN");
      }
    } catch (err) {
      console.error("Error setting PIN:", err);
      setError("An error occurred while setting the PIN");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative w-full max-w-md transform rounded-lg bg-white px-6 py-6 shadow-xl transition-all">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                <KeyIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">Set PIN</h3>
                <p className="text-sm text-gray-500">
                  For {user.full_name} ({user.user_code})
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="pin"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                New PIN
              </label>
              <input
                type="password"
                id="pin"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••"
                className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm text-center tracking-widest"
                maxLength={4}
                autoComplete="off"
              />
            </div>

            <div>
              <label
                htmlFor="confirmPin"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirm PIN
              </label>
              <input
                type="password"
                id="confirmPin"
                value={confirmPin}
                onChange={(e) =>
                  setConfirmPin(e.target.value.replace(/\D/g, ""))
                }
                placeholder="••••"
                className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm text-center tracking-widest"
                maxLength={4}
                autoComplete="off"
              />
            </div>

            <div className="bg-gray-50 rounded-md p-3">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                PIN Requirements:
              </h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Must be exactly 4 digits</li>
                <li>• Cannot be all the same digit (1111)</li>
                <li>• Cannot be sequential (1234, 4321)</li>
                <li>• Will expire in 90 days</li>
              </ul>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  pin.length !== 4 || confirmPin.length !== 4 || loading
                }
                className="flex-1 flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Set PIN
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const PinManagement: React.FC<PinManagementProps> = ({ isOpen, onClose }) => {
  const { user: _currentUser } = useAuth();
  const [users, setUsers] = useState<UserWithPinStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithPinStatus | null>(
    null,
  );
  const [setPinModalOpen, setSetPinModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const usersData = await getUsersWithPinStatus();
      setUsers(usersData);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleSetPin = (user: UserWithPinStatus) => {
    setSelectedUser(user);
    setSetPinModalOpen(true);
  };

  const handlePinSet = () => {
    loadUsers(); // Refresh the list
  };

  const getPinStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <ShieldCheckIcon className="h-5 w-5 text-green-500" />;
      case "expired":
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case "not_set":
        return <ShieldExclamationIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ShieldExclamationIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getPinStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "Active";
      case "expired":
        return "Expired";
      case "not_set":
        return "Not Set";
      default:
        return "Unknown";
    }
  };

  const getPinStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-green-700 bg-green-50 border-green-200";
      case "expired":
        return "text-yellow-700 bg-yellow-50 border-yellow-200";
      case "not_set":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-40 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={onClose}
          />

          <div className="relative w-full max-w-4xl transform rounded-lg bg-white shadow-xl transition-all">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <KeyIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    PIN Management
                  </h3>
                  <p className="text-sm text-gray-500">
                    Manage user PINs for quick switching
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  <span className="ml-2 text-gray-600">Loading users...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {users.length === 0 ? (
                    <div className="text-center py-12">
                      <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No users found
                      </h3>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {users.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                              <UserIcon className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">
                                {user.full_name}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {user.email} • Code: {user.user_code}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              {getPinStatusIcon(user.pin_status)}
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPinStatusColor(
                                  user.pin_status,
                                )}`}
                              >
                                {getPinStatusText(user.pin_status)}
                              </span>
                            </div>

                            {user.pin_expires_at &&
                              user.pin_status === "active" && (
                                <div className="text-xs text-gray-500">
                                  Expires:{" "}
                                  {new Date(
                                    user.pin_expires_at,
                                  ).toLocaleDateString()}
                                </div>
                              )}

                            <button
                              onClick={() => handleSetPin(user)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <KeyIcon className="h-3 w-3 mr-1" />
                              {user.pin_status === "not_set"
                                ? "Set PIN"
                                : "Reset PIN"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 rounded-b-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  PINs are required for quick user switching
                </p>
                <button
                  onClick={loadUsers}
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Set PIN Modal */}
      <SetPinModal
        user={selectedUser}
        isOpen={setPinModalOpen}
        onClose={() => {
          setSetPinModalOpen(false);
          setSelectedUser(null);
        }}
        onPinSet={handlePinSet}
      />
    </>
  );
};

export default PinManagement;
